import React, { useEffect, useState, useCallback } from "react";
import { Building2, ClipboardCheck, CheckCircle2, Clock, Camera, X, MapPin } from "lucide-react";
import { api, openFileById } from "@/lib/api";
import { toast } from "sonner";
import OfficerShell from "./OfficerShell";

export const CHECKLIST = [
  "प्रस्तावित कार्यालयाच्या जागेचा पत्ता",
  "कार्यालयाची जागा घर किंवा इतर वापरापासून स्वतंत्र आहे का?",
  "कार्यालय खोलीचे क्षेत्रफळ किमान १०० चौ. फूट आहे का?",
  "किमान १०० चौ.फूट क्षेत्रफळाचे स्वतंत्र कार्यालय (८-अ उतारा / असेसमेंट उतारा / भाडेकरार)",
  "दोन संगणक / लॅपटॉप",
  "लेझर प्रिंटर",
  "स्कॅनर",
  "बायोमेट्रिक उपकरण",
  "वेबकॅम",
  "यूपीएस अथवा पर्यायी वीज बॅकअप",
  "ब्रॉडबँड इंटरनेट (ग्रामीण १० Mbps, नागरी २० Mbps)",
  "सीसीटीव्ही व्यवस्था",
  "दिव्यांग व ज्येष्ठ नागरिकांसाठी सुलभ प्रवेश",
  "नागरिकांसाठी प्रतीक्षा कक्ष",
  "शासन प्राधिकृत सेवा शुल्क फलक व बाहेर नामफलक",
];

const officeAddr = (a) => a.data?.officeAddress || a.data?.address || a.data?.village || a.data?.city || "—";
const applicant = (a) => a.data?.nameMr || a.applicantName || "—";
const localBody = (a) => a.data?.village || a.data?.city || "इतर";

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="rounded-xl border border-[#e6dcc4] bg-white p-4">
      <Icon className={`mb-1.5 h-5 w-5 ${color}`} />
      <p className={`text-[24px] font-bold ${color}`}>{value}</p>
      <p className="text-[12.5px] text-slate-500">{label}</p>
    </div>
  );
}

// ---------------- Photo uploader ----------------
function PhotoUpload({ appId, field, value, onChange, label }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.circleUploadPhoto(appId, field, file);
      onChange({ fileId: res.fileId, name: res.name });
    } catch (err) {
      toast.error("फोटो अपलोड अयशस्वी: " + (err.message || ""));
    }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-dashed border-slate-300 p-3">
      <p className="mb-2 text-[13px] font-semibold text-slate-700">{label} <span className="text-red-500">*</span></p>
      {value?.fileId ? (
        <div className="flex items-center gap-3">
          <img src={api.fileUrl(value.fileId)} alt="preview" className="h-24 w-32 rounded-lg object-cover ring-1 ring-slate-200" />
          <button type="button" onClick={() => onChange(null)} className="rounded-full border border-red-200 px-3 py-1 text-[12px] font-semibold text-red-600 hover:bg-red-50">काढा</button>
        </div>
      ) : (
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[#1b5e3f]">
          <Camera className="h-4 w-4" />
          <span>{busy ? "अपलोड होत आहे..." : "फोटो निवडा / काढा (कमाल 1MB)"}</span>
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} disabled={busy} data-testid={`circle-photo-${field}`} />
        </label>
      )}
    </div>
  );
}

// ---------------- Verification report form ----------------
function CircleVerifyForm({ appId, officerName, onDone, onBack }) {
  const [app, setApp] = useState(null);
  const [rows, setRows] = useState(CHECKLIST.map((label, i) => ({ n: i + 1, label, verify: "" })));
  const [officer, setOfficer] = useState({ officerName: officerName || "", designation: "मंडळ अधिकारी", inspectionDate: new Date().toISOString().slice(0, 10), inspectionTime: "" });
  const [recommendation, setRecommendation] = useState("");
  const [remark, setRemark] = useState("");
  const [photoOutside, setPhotoOutside] = useState(null);
  const [photoInside, setPhotoInside] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.officerDetail(appId).then(setApp).catch(() => {});
  }, [appId]);

  const setRow = (i, key, val) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));

  const submit = async () => {
    if (!recommendation) { toast.error("कृपया एकंदर अभिप्राय निवडा."); return; }
    if (recommendation === "बदल" && !remark.trim()) { toast.error("अर्जदाराच्या माहितीमध्ये बदल असल्यास शेरा लिहिणे आवश्यक आहे."); return; }
    if (!photoOutside?.fileId || !photoInside?.fileId) { toast.error("दोन्ही स्थळ पाहणी फोटो अपलोड करणे सक्तीचे आहे."); return; }
    const dd = app?.data || {};
    const checklist = rows.map((row, i) => {
      let detail;
      if (i === 0) {
        detail = dd.office_address || dd.officeAddress || dd.address || "—";
      } else {
        const av = dd[`infra_${i - 1}`]; const aq = dd[`infra_qty_${i - 1}`];
        detail = av === "yes" ? `होय${aq ? " — " + aq : ""}` : av === "no" ? "नाही" : "—";
      }
      return { ...row, applicantDetail: detail };
    });
    setSaving(true);
    try {
      await api.circleVerify(appId, { checklist, ...officer, recommendation, remark, photoOutside, photoInside });
      toast.success("पायाभूत सुविधा तपासणी अहवाल जतन झाला व तहसीलदार यांच्याकडे पाठवला.");
      onDone();
    } catch (err) {
      toast.error("जतन अयशस्वी: " + (err.message || ""));
    }
    setSaving(false);
  };

  if (!app) return <div className="rounded-2xl bg-white p-10 text-center text-slate-400">लोड होत आहे...</div>;
  const d = app.data || {};

  return (
    <div>
      <button onClick={onBack} data-testid="circle-back" className="mb-3 rounded-full border border-slate-300 px-4 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">← यादीकडे परत</button>

      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="circle-verify-form">
        <div className="mb-3 text-center">
          <p className="text-[13px] text-slate-500">महाराष्ट्र शासन · जिल्हाधिकारी कार्यालय, अमरावती</p>
          <p className="text-[16px] font-bold text-[#1b5e3f]">पायाभूत सुविधा — प्रत्यक्ष स्थळ पडताळणी अहवाल</p>
          <p className="text-[12px] text-slate-400">(मंडळ अधिकारी यांच्यामार्फत प्रत्यक्ष स्थळ भेट देऊन भरावयाचा नमुना)</p>
        </div>

        {/* applicant header */}
        <div className="mb-4 grid gap-x-8 gap-y-1 rounded-xl bg-[#faf8f0] p-4 md:grid-cols-2">
          {[["अर्ज क्रमांक", app.applicationNumber], ["अर्जदाराचे नाव", d.nameMr || app.applicantName],
            ["मोबाईल", d.mobile], ["तालुका", app.tehsil], ["जिल्हा", "अमरावती"],
            ["स्थानिक स्वराज्य संस्था प्रकार", d.orgType], ["पिनकोड", d.pincode],
            ["प्रस्तावित कार्यालयाचा पत्ता", d.office_address || officeAddr(app)],
            ["अक्षांश (Latitude)", d.office_lat], ["रेखांश (Longitude)", d.office_lng]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1">
              <span className="text-[12.5px] text-slate-500">{k}</span>
              <span className="text-right text-[13px] font-medium text-slate-800">{v || "—"}</span>
            </div>
          ))}
        </div>

        {/* checklist */}
        <p className="mb-2 rounded bg-[#1b5e3f] px-3 py-1.5 text-[13.5px] font-bold text-white">४. पायाभूत सुविधा — तपासणी सूची</p>
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-100 text-left text-slate-600">
                <th className="p-2">अ.क्र.</th>
                <th className="p-2">तपासणीचा मुद्दा</th>
                <th className="p-2">अर्जदाराने नमूद केलेला तपशील</th>
                <th className="p-2 text-center">प्रत्यक्ष पडताळणी</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isAddr = i === 0;
                const av = isAddr ? null : d[`infra_${i - 1}`];
                const aq = isAddr ? null : d[`infra_qty_${i - 1}`];
                const af = isAddr ? null : d[`infra_file_${i - 1}`];
                const detail = isAddr
                  ? (d.office_address || d.officeAddress || d.address || "—")
                  : (av === "yes" ? `होय${aq ? " — " + aq : ""}` : av === "no" ? "नाही" : "—");
                const detailCls = isAddr ? "text-slate-700"
                  : (av === "yes" ? "font-semibold text-emerald-700" : av === "no" ? "font-semibold text-red-600" : "text-slate-400");
                const opts = isAddr ? ["पत्ता बरोबर आहे", "नाही"] : ["आहे", "नाही"];
                return (
                  <tr key={row.n} className="border-b border-slate-100 align-top">
                    <td className="p-2">{row.n}</td>
                    <td className="p-2">{row.label}</td>
                    <td className="p-2">
                      <span className={detailCls}>{detail}</span>
                      {af?.fileId && (
                        <button type="button" onClick={() => openFileById(af.fileId)} className="ml-2 text-[11.5px] text-[#c2570a] underline">पुरावा</button>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap justify-center gap-2">
                        {opts.map((opt) => (
                          <button key={opt} type="button" onClick={() => setRow(i, "verify", opt)}
                            data-testid={`circle-check-${row.n}-${opt === "नाही" ? "no" : "yes"}`}
                            className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${row.verify === opt ? (opt === "नाही" ? "border-red-500 bg-red-500 text-white" : "border-emerald-600 bg-emerald-600 text-white") : "border-slate-300 bg-white text-slate-500"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* officer details */}
        <p className="mb-2 rounded bg-[#1b5e3f] px-3 py-1.5 text-[13.5px] font-bold text-white">पडताळणी अधिकारी तपशील</p>
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <Labeled label="अधिकाऱ्याचे नाव"><input value={officer.officerName} onChange={(e) => setOfficer({ ...officer, officerName: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" data-testid="circle-officer-name" /></Labeled>
          <Labeled label="पदनाम"><input value={officer.designation} onChange={(e) => setOfficer({ ...officer, designation: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" /></Labeled>
          <Labeled label="प्रत्यक्ष स्थळ पाहणी दिनांक"><input type="date" value={officer.inspectionDate} onChange={(e) => setOfficer({ ...officer, inspectionDate: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" data-testid="circle-inspection-date" /></Labeled>
          <Labeled label="स्थळ पाहणीची वेळ"><input type="time" value={officer.inspectionTime} onChange={(e) => setOfficer({ ...officer, inspectionTime: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" /></Labeled>
        </div>

        {/* photos */}
        <p className="mb-2 rounded bg-[#c2570a] px-3 py-1.5 text-[13.5px] font-bold text-white">स्थळ पाहणी फोटो (सक्तीचे)</p>
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <PhotoUpload appId={appId} field="photoOutside" value={photoOutside} onChange={setPhotoOutside} label="१. कार्यालयाचा बाहेरील दर्शनी फोटो" />
          <PhotoUpload appId={appId} field="photoInside" value={photoInside} onChange={setPhotoInside} label="२. कार्यालयाचा आतील फोटो (उपकरणांसह)" />
        </div>

        {/* recommendation */}
        <p className="mb-2 rounded bg-[#1b5e3f] px-3 py-1.5 text-[13.5px] font-bold text-white">एकंदर अभिप्राय</p>
        <div className="mb-3 flex flex-col gap-3 sm:flex-row">
          {[["योग्य", "अर्जदाराने दिलेली माहिती योग्य आहे"], ["बदल", "अर्जदाराने दिलेल्या माहितीमध्ये बदल आहे"]].map(([val, lbl]) => {
            const active = recommendation === val;
            const cls = val === "योग्य"
              ? (active ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white text-slate-600")
              : (active ? "border-amber-600 bg-amber-600 text-white" : "border-slate-300 bg-white text-slate-600");
            return (
              <button key={val} type="button" onClick={() => setRecommendation(val)} data-testid={`circle-rec-${val}`}
                className={`flex-1 rounded-xl border px-4 py-3 text-[13.5px] font-semibold ${cls}`}>
                {lbl}
              </button>
            );
          })}
        </div>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3}
          placeholder={recommendation === "बदल" ? "शेरा (आवश्यक) — कोणता बदल आढळला ते नमूद करा" : "शेरा / अभिप्राय (ऐच्छिक)"}
          data-testid="circle-remark"
          className={`mb-4 w-full rounded-lg border px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f] ${recommendation === "बदल" && !remark.trim() ? "border-amber-400" : "border-slate-200"}`} />

        <button onClick={submit} disabled={saving} data-testid="circle-submit"
          className="w-full rounded-full bg-[#1b5e3f] py-3 text-[15px] font-bold text-white hover:bg-[#144d33] disabled:opacity-60">
          {saving ? "जतन होत आहे..." : "अहवाल जतन करा व तहसीलदार यांच्याकडे पाठवा"}
        </button>
      </div>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-[13px] font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

export default function CircleDashboard() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({ applications: [], stats: { total: 0, done: 0, pending: 0 } });
  const [loading, setLoading] = useState(true);
  const [bodyFilter, setBodyFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([api.officerMeta(), api.circleApplications()]);
      setMeta(m); setData(d);
    } catch (e) { /* noop */ }
    setLoading(false);
    setOpenId(null);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return (
      <OfficerShell>
        <CircleVerifyForm appId={openId} officerName={meta?.name} onDone={load} onBack={() => setOpenId(null)} />
      </OfficerShell>
    );
  }

  const apps = data.applications || [];
  const bodies = Array.from(new Set(apps.map(localBody)));
  const filtered = bodyFilter ? apps.filter((a) => localBody(a) === bodyFilter) : apps;

  return (
    <OfficerShell>
      <div className="mb-1 text-[18px] font-bold text-[#1b5e3f]">मंडळ अधिकारी डॅशबोर्ड</div>
      <p className="mb-4 text-[13px] text-slate-500">{meta?.circleLabel || "मंडळ"} · तहसील {meta?.tehsil || ""} — पायाभूत सुविधा प्रत्यक्ष स्थळ पडताळणी</p>

      {/* stat cards */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={ClipboardCheck} value={data.stats.total} label="एकूण अर्ज (पडताळणीसाठी)" color="text-[#1b5e3f]" />
        <StatCard icon={CheckCircle2} value={data.stats.done} label="तपासणी झालेले" color="text-emerald-600" />
        <StatCard icon={Clock} value={data.stats.pending} label="तपासणी प्रलंबीत" color="text-amber-600" />
      </div>

      {/* local body buttons */}
      <div className="mb-5">
        <p className="mb-2 text-[13px] font-semibold text-slate-600">मंडळाखालील स्थानिक स्वराज्य संस्था</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setBodyFilter("")} data-testid="circle-body-all"
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[13px] font-semibold ${!bodyFilter ? "border-[#1b5e3f] bg-[#1b5e3f] text-white" : "border-slate-300 bg-white text-slate-600"}`}>
            <Building2 className="h-3.5 w-3.5" /> सर्व ({apps.length})
          </button>
          {bodies.map((b) => (
            <button key={b} onClick={() => setBodyFilter(b)} data-testid={`circle-body-${b}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[13px] font-semibold ${bodyFilter === b ? "border-[#1b5e3f] bg-[#1b5e3f] text-white" : "border-slate-300 bg-white text-slate-600"}`}>
              <MapPin className="h-3.5 w-3.5" /> {b} ({apps.filter((a) => localBody(a) === b).length})
            </button>
          ))}
        </div>
      </div>

      {/* list */}
      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">पायाभूत सुविधा तपासणीसाठी अर्ज</p>
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="circle-empty">
            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
            <p className="text-[14px] font-semibold text-emerald-700">तपासणीसाठी प्रलंबीत अर्ज नाहीत.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="circle-list-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">अर्ज क्रमांक</th>
                  <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                  <th className="p-2.5 font-semibold">कार्यालयाचा पत्ता</th>
                  <th className="p-2.5 text-center font-semibold">तपासणी</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}<br /><span className="text-[12px] text-slate-400">{a.data?.mobile}</span></td>
                    <td className="p-2.5 text-slate-600">{officeAddr(a)}</td>
                    <td className="p-2.5 text-center">
                      {a.visitDone ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-[12px] font-semibold text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" /> पूर्ण ({a.visitDecision || "—"})
                        </span>
                      ) : (
                        <button onClick={() => setOpenId(a.id)} data-testid={`circle-verify-${a.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-[#c2570a] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90">
                          <ClipboardCheck className="h-3.5 w-3.5" /> तपासणी करा
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </OfficerShell>
  );
}
